import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  Banknote,
  BookOpenCheck,
  Briefcase,
  CheckCircle2,
  Compass,
  Loader2,
  MapPin,
  Sparkles,
  Target,
  User,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function labelize(s) {
  if (s == null) return null;
  return String(s)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtMoney(amount, currency = "USD") {
  if (amount == null) return null;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${currency || ""}`.trim();
  }
}

function joinNonEmpty(parts, sep = " · ") {
  return parts.filter(Boolean).join(sep);
}

function deriveFinancialUrgency(fp = {}) {
  const r = fp.runway_months;
  let level = "unknown";
  let copy = "Not enough info yet.";
  if (r != null) {
    if (r < 3) {
      level = "high";
      copy = `Less than 3 months of runway — let's prioritise income first, retraining second.`;
    } else if (r < 6) {
      level = "moderate";
      copy = `${r} months of runway — there's room to be deliberate, but not too patient.`;
    } else {
      level = "low";
      copy = `${r} months of runway — you can afford a more strategic transition.`;
    }
  }
  if (fp.has_dependents === true && fp.has_savings_buffer === false && level !== "high") {
    level = level === "low" ? "moderate" : "high";
    copy += " Dependents + no savings buffer raises the urgency.";
  }
  if (fp.has_debt_stress === true && level === "low") {
    level = "moderate";
    copy += " Debt stress noted.";
  }
  return { level, copy };
}

function deriveCandidateSummary(d) {
  const cs = d.current_situation || {};
  const goals = d.career_goals || {};
  const yearsTotal = (d.work_history || [])
    .map((r) => {
      const s = r.start_year;
      const e = r.end_year || new Date().getFullYear();
      return s ? Math.max(0, e - s) : 0;
    })
    .reduce((a, b) => a + b, 0);
  const topSkills = (d.skills || [])
    .slice(0, 3)
    .map((s) => s.name)
    .filter(Boolean);

  const bits = [];
  if (cs.last_role) bits.push(cs.last_role);
  if (cs.last_industry) bits.push(`in ${cs.last_industry}`);
  if (yearsTotal > 0) bits.push(`with ~${yearsTotal} years of experience`);
  if (cs.employment_status) bits.push(`currently ${labelize(cs.employment_status).toLowerCase()}`);

  const sentences = [];
  if (bits.length > 0) sentences.push(bits.join(", ") + ".");
  if (topSkills.length > 0)
    sentences.push(`Strongest in ${topSkills.join(", ")}.`);
  if (goals.short_term_3_6_months)
    sentences.push(`Short-term goal: ${goals.short_term_3_6_months}`);

  if (sentences.length === 0) {
    sentences.push("Your assessment is in — we'll build your plan from here.");
  }
  return sentences.join(" ");
}

const URGENCY_STYLES = {
  high: "bg-red-50 border-red-100 text-red-800",
  moderate: "bg-amber-50 border-amber-100 text-amber-800",
  low: "bg-emerald-50 border-emerald-100 text-emerald-800",
  unknown: "bg-slate-100 border-slate-200 text-slate-700",
};

function SectionCard({ icon: Icon, title, eyebrow, accent = "bg-sky-100 text-sky-700", testid, children }) {
  return (
    <article
      className="rounded-2xl border border-slate-200 bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      data-testid={testid}
    >
      <div className="flex items-center gap-3 mb-4">
        <span
          className={`inline-flex items-center justify-center h-10 w-10 rounded-xl ${accent}`}
        >
          <Icon className="h-5 w-5" />
        </span>
        <div>
          {eyebrow ? (
            <p className="text-xs uppercase tracking-[0.18em] text-slate-500 font-medium">
              {eyebrow}
            </p>
          ) : null}
          <h3 className="text-lg font-medium text-slate-900">{title}</h3>
        </div>
      </div>
      {children}
    </article>
  );
}

function Empty({ children }) {
  return <p className="text-sm text-slate-500 italic">{children}</p>;
}

function Pill({ children, testid }) {
  return (
    <span
      className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 px-3 py-1 text-sm text-slate-700"
      data-testid={testid}
    >
      {children}
    </span>
  );
}

export default function AssessmentReview() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: doc } = await api.get("/assessment/me");
        if (!active) return;
        setData(doc);
        if (doc.ai_recommendation) setGenerated(true);
      } catch (err) {
        if (!active) return;
        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const onGenerateReport = async () => {
    if (!data || generating) return;
    setGenerating(true);
    setError("");
    try {
      const { data: doc } = await api.post("/assessment/me/generate-report");
      setData(doc);
      setGenerated(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setGenerating(false);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="assessment-review-page">
        <Navbar />
        <main className="pt-24 lg:pt-28 pb-20">
          <div className="max-w-5xl mx-auto px-6 lg:px-12 flex items-center gap-3 text-slate-500">
            {error ? (
              <span className="text-red-700">{error}</span>
            ) : (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading your review…
              </>
            )}
          </div>
        </main>
      </div>
    );
  }

  // Guard: if assessment isn't submitted yet, send back to the form
  if (data.status === "draft") {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="assessment-review-page">
        <Navbar />
        <main className="pt-24 lg:pt-28 pb-20">
          <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center" data-testid="review-not-submitted">
            <h1 className="text-2xl font-medium text-slate-900">
              {"You haven't submitted your assessment yet."}
            </h1>
            <p className="mt-3 text-slate-600">
              Finish the questionnaire to see your personalised review.
            </p>
            <Link
              to="/assessment"
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-medium text-white shadow-md hover:bg-slate-800"
              data-testid="review-go-to-assessment"
            >
              Go to assessment
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const cs = data.current_situation || {};
  const skills = data.skills || [];
  const salary = data.salary || {};
  const loc = data.location_travel_relocation || {};
  const retrain = data.retraining_capacity || {};
  const fp = data.financial_position || {};
  const goals = data.career_goals || {};

  const summary = deriveCandidateSummary(data);
  const urgency = deriveFinancialUrgency(fp);

  const arrangements = [
    loc.open_to_remote && "Remote",
    loc.open_to_hybrid && "Hybrid",
    loc.open_to_onsite && "Onsite",
    loc.open_to_relocation && "Relocation",
  ].filter(Boolean);

  const locationLine = joinNonEmpty([loc.current_city, loc.current_region, loc.current_country], ", ");

  return (
    <div className="min-h-screen bg-slate-50" data-testid="assessment-review-page">
      <Navbar />
      <main className="pt-24 lg:pt-28 pb-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <Link
            to="/dashboard"
            className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1.5"
            data-testid="review-back-to-dashboard"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to dashboard
          </Link>

          <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm uppercase tracking-[0.18em] text-sky-700 font-medium">
                Assessment review
              </p>
              <h1
                className="mt-1.5 text-3xl lg:text-4xl font-medium tracking-tight text-slate-900"
                data-testid="review-heading"
              >
                {`Here's what we heard, ${user?.name?.split(/[ @]/)[0] || "there"}.`}
              </h1>
              <p className="mt-2 text-base text-slate-600 max-w-2xl">
                A quick recap before we generate your personalised career report.
                If anything looks off, you can{" "}
                <Link
                  to="/assessment"
                  className="text-slate-900 underline hover:no-underline"
                  data-testid="review-edit-link"
                >
                  edit your answers
                </Link>
                .
              </p>
            </div>
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800"
              data-testid="review-status-pill"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              {labelize(data.status)}
            </span>
          </div>

          {error ? (
            <div
              className="mt-6 rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700"
              data-testid="review-error"
            >
              {error}
            </div>
          ) : null}

          {/* Section 1: Candidate summary */}
          <div className="mt-10">
            <SectionCard
              icon={User}
              eyebrow="Section 1"
              title="Candidate summary"
              accent="bg-slate-900 text-white"
              testid="review-candidate-summary"
            >
              <p className="text-base text-slate-700 leading-relaxed" data-testid="review-candidate-summary-text">
                {summary}
              </p>
            </SectionCard>
          </div>

          {/* Two-column sections */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Key skills */}
            <SectionCard
              icon={Briefcase}
              eyebrow="Section 2"
              title="Key skills"
              accent="bg-sky-100 text-sky-700"
              testid="review-key-skills"
            >
              {skills.length === 0 ? (
                <Empty>No skills added yet.</Empty>
              ) : (
                <ul className="flex flex-wrap gap-2" data-testid="review-skills-list">
                  {skills.map((s, i) => (
                    <li key={`${s.name}-${i}`}>
                      <Pill testid={`review-skill-${i}`}>
                        {s.name}
                        {s.proficiency ? (
                          <span className="ml-2 text-xs text-slate-500">
                            {labelize(s.proficiency)}
                          </span>
                        ) : null}
                        {s.years ? (
                          <span className="ml-2 text-xs text-slate-500">
                            {`${s.years} yr${s.years === 1 ? "" : "s"}`}
                          </span>
                        ) : null}
                      </Pill>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>

            {/* Salary needs */}
            <SectionCard
              icon={Banknote}
              eyebrow="Section 3"
              title="Salary needs"
              accent="bg-emerald-100 text-emerald-700"
              testid="review-salary"
            >
              {salary.minimum_annual == null && salary.target_annual == null ? (
                <Empty>No salary expectations provided.</Empty>
              ) : (
                <dl className="grid grid-cols-2 gap-4">
                  <div data-testid="review-salary-minimum">
                    <dt className="text-xs uppercase tracking-wider text-slate-500">
                      Minimum
                    </dt>
                    <dd className="mt-1 text-lg font-medium text-slate-900">
                      {fmtMoney(salary.minimum_annual, salary.currency) || "—"}
                    </dd>
                  </div>
                  <div data-testid="review-salary-target">
                    <dt className="text-xs uppercase tracking-wider text-slate-500">
                      Target
                    </dt>
                    <dd className="mt-1 text-lg font-medium text-slate-900">
                      {fmtMoney(salary.target_annual, salary.currency) || "—"}
                    </dd>
                  </div>
                </dl>
              )}
              {salary.open_to_lower_during_transition === true ? (
                <p className="mt-4 text-sm text-slate-600">
                  Open to taking less while transitioning.
                </p>
              ) : null}
            </SectionCard>

            {/* Location preferences */}
            <SectionCard
              icon={MapPin}
              eyebrow="Section 4"
              title="Location preferences"
              accent="bg-violet-100 text-violet-700"
              testid="review-location"
            >
              {locationLine ? (
                <p className="text-base text-slate-700" data-testid="review-location-line">
                  {locationLine}
                </p>
              ) : (
                <Empty>No location provided.</Empty>
              )}
              {arrangements.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="review-arrangements">
                  {arrangements.map((a) => (
                    <Pill key={a}>{a}</Pill>
                  ))}
                </div>
              ) : null}
              {loc.relocation_radius_km ? (
                <p className="mt-3 text-sm text-slate-600">
                  {`Relocation radius: ${loc.relocation_radius_km} km`}
                </p>
              ) : null}
              {loc.travel_tolerance_pct != null ? (
                <p className="mt-1 text-sm text-slate-600">
                  {`Travel tolerance: ${loc.travel_tolerance_pct}%`}
                </p>
              ) : null}
            </SectionCard>

            {/* Retraining limits */}
            <SectionCard
              icon={BookOpenCheck}
              eyebrow="Section 5"
              title="Retraining limits"
              accent="bg-amber-100 text-amber-700"
              testid="review-retraining"
            >
              <dl className="grid grid-cols-2 gap-4">
                <div data-testid="review-retraining-hours">
                  <dt className="text-xs uppercase tracking-wider text-slate-500">
                    Hours / week
                  </dt>
                  <dd className="mt-1 text-lg font-medium text-slate-900">
                    {retrain.hours_per_week_available ?? "—"}
                  </dd>
                </div>
                <div data-testid="review-retraining-budget">
                  <dt className="text-xs uppercase tracking-wider text-slate-500">
                    Budget
                  </dt>
                  <dd className="mt-1 text-lg font-medium text-slate-900">
                    {retrain.budget_for_courses != null
                      ? fmtMoney(retrain.budget_for_courses, salary.currency)
                      : "—"}
                  </dd>
                </div>
              </dl>
              {Array.isArray(retrain.preferred_formats) &&
              retrain.preferred_formats.length > 0 ? (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="review-retraining-formats">
                  {retrain.preferred_formats.map((f) => (
                    <Pill key={f}>{labelize(f)}</Pill>
                  ))}
                </div>
              ) : null}
            </SectionCard>
          </div>

          {/* Financial urgency — full width, colour-coded */}
          <div className="mt-6">
            <SectionCard
              icon={AlertTriangle}
              eyebrow="Section 6"
              title="Financial urgency"
              accent="bg-rose-100 text-rose-700"
              testid="review-financial-urgency"
            >
              <div
                className={`rounded-xl border px-4 py-3 ${URGENCY_STYLES[urgency.level]}`}
                data-testid={`review-financial-urgency-level-${urgency.level}`}
              >
                <p className="text-xs uppercase tracking-wider font-medium">
                  Urgency
                </p>
                <p className="mt-0.5 text-lg font-medium">
                  {labelize(urgency.level)}
                </p>
                <p className="mt-2 text-sm leading-relaxed">{urgency.copy}</p>
              </div>
            </SectionCard>
          </div>

          {/* Career goals */}
          <div className="mt-6">
            <SectionCard
              icon={Target}
              eyebrow="Section 7"
              title="Career goals"
              accent="bg-indigo-100 text-indigo-700"
              testid="review-career-goals"
            >
              <div className="space-y-4">
                <GoalLine
                  label="Next 3–6 months"
                  value={goals.short_term_3_6_months}
                  testid="review-goal-short"
                />
                <GoalLine
                  label="1–2 years"
                  value={goals.medium_term_1_2_years}
                  testid="review-goal-medium"
                />
                <GoalLine
                  label="5 years"
                  value={goals.long_term_5_years}
                  testid="review-goal-long"
                />
                {Array.isArray(goals.must_haves) && goals.must_haves.length > 0 ? (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
                      Must-haves
                    </p>
                    <div className="flex flex-wrap gap-2" data-testid="review-must-haves">
                      {goals.must_haves.map((m) => (
                        <Pill key={m}>{m}</Pill>
                      ))}
                    </div>
                  </div>
                ) : null}
                {Array.isArray(goals.deal_breakers) && goals.deal_breakers.length > 0 ? (
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
                      Deal-breakers
                    </p>
                    <div className="flex flex-wrap gap-2" data-testid="review-deal-breakers">
                      {goals.deal_breakers.map((m) => (
                        <Pill key={m}>{m}</Pill>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </SectionCard>
          </div>

          {/* Generate report CTA */}
          <section
            className="mt-10 rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-800 text-white p-8 lg:p-12 shadow-[0_20px_60px_rgb(0,0,0,0.10)]"
            data-testid="review-generate-cta"
          >
            <div className="flex items-start gap-4">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10">
                <Sparkles className="h-5 w-5" />
              </span>
              <div className="flex-1">
                <h2 className="text-2xl font-medium tracking-tight">
                  Generate My Career Report
                </h2>
                <p className="mt-2 text-base text-slate-300 max-w-2xl">
                  {"We'll turn your answers into a calm, prioritised plan — career directions that fit you, roles to consider, and the smallest next step for each."}
                </p>
                {generated ? (
                  <div
                    className="mt-6 rounded-xl bg-white/10 border border-white/10 px-4 py-3"
                    data-testid="review-report-generated"
                  >
                    <p className="text-sm font-medium text-emerald-200 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" />
                      Report ready
                    </p>
                    {data.ai_recommendation?.pathways ? (
                      <div className="mt-2 text-sm text-slate-300 space-y-1">
                        {["immediate_income", "transitional", "long_term_future_proof"].map((k) => {
                          const role = data.ai_recommendation.pathways?.[k]?.role_title;
                          if (!role) return null;
                          const label =
                            k === "immediate_income"
                              ? "Immediate"
                              : k === "transitional"
                              ? "Transitional"
                              : "Long-term";
                          return (
                            <p key={k} data-testid={`review-report-${k.replace(/_/g, "-")}`}>
                              <span className="text-slate-400">{label}:</span> {role}
                            </p>
                          );
                        })}
                        {Array.isArray(data.ai_recommendation.providers) ? (
                          <p
                            className="pt-1 text-xs text-slate-400"
                            data-testid="review-report-providers"
                          >
                            {`Consulted ${data.ai_recommendation.providers.length} providers${
                              data.ai_recommendation.all_mocked ? " (placeholder mode)" : ""
                            }.`}
                          </p>
                        ) : null}
                      </div>
                    ) : (
                      <p className="mt-1 text-sm text-slate-300">
                        Your report is ready. Open the dashboard or scroll up to review the answers it was built from.
                      </p>
                    )}
                  </div>
                ) : null}
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    onClick={onGenerateReport}
                    disabled={generating}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-medium text-slate-900 shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-70"
                    data-testid="review-generate-report-button"
                  >
                    {generating ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Compass className="h-4 w-4" />
                    )}
                    {generating
                      ? "Queuing your report…"
                      : generated
                      ? "Regenerate report"
                      : "Generate My Career Report"}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard")}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/20 bg-transparent px-5 py-3 text-sm font-medium text-white hover:bg-white/10 transition-all"
                    data-testid="review-go-dashboard-button"
                  >
                    Go to dashboard
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

function GoalLine({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">
        {label}
      </p>
      <p className="mt-0.5 text-base text-slate-800">
        {value ? value : <span className="text-slate-400 italic">Not provided</span>}
      </p>
    </div>
  );
}
